import { FEATURE_GATE_RULE_TYPE, type FeatureGateRuleNode } from './ruleTree';

/**
 * Pure helpers that translate between the v1 form's two-slider state
 * (admins rollout %, everyone rollout %) and the recursive rule tree
 * stored in `feature_gates.rule_tree`. Kept separate from the form
 * components so the canonicalization rules can be unit-tested in
 * isolation, and so the v2 recursive builder can drop in by replacing
 * the form layer without touching the storage shape.
 *
 * Canonicalization table (form → rule):
 *
 * | Admins % | Everyone % | Emitted rule                                                |
 * | -------- | ---------- | ----------------------------------------------------------- |
 * | X > 0    | 0          | `{ type: 'is_admin', rolloutPercent: X }`                   |
 * | 0        | Y > 0      | `{ type: 'all_users', rolloutPercent: Y }`                  |
 * | X > 0    | Y > 0      | `{ type: 'or', children: [is_admin@X, all_users@Y] }`       |
 * | 0        | 0          | Caller blocks save (UI-level constraint, not encoded here). |
 *
 * Inverse direction (`ruleToFormState`) recognizes exactly the same
 * three canonical shapes. Anything else (e.g. an operator hand-edited a
 * `country_in` rule via raw GraphQL) returns `isCanonical: false` so
 * the edit form can render the non-canonical banner without losing
 * access to the rest of the gate's editable fields.
 */

export interface IFormState {
  /** Integer 0-100. Maps to an `is_admin` leaf when > 0. */
  adminPct: number;
  /** Integer 0-100. Maps to an `all_users` leaf when > 0. */
  everyonePct: number;
}

export interface IRuleParseResult extends IFormState {
  /**
   * `true` when the rule tree matches one of the three canonical shapes
   * the v1 form emits. `false` means the form cannot represent the rule
   * (e.g. a future leaf type like `country_in`, or a hand-edited
   * non-canonical composition) — the caller should render the banner
   * and fall back to read-only display of the rule.
   */
  isCanonical: boolean;
}

const ZERO_FORM_STATE: IFormState = { adminPct: 0, everyonePct: 0 };

/**
 * Builds the canonical rule tree for the supplied admin / everyone
 * percentages. Returns `null` when both sliders are zero — the caller
 * (form) blocks save in that case rather than emitting a degenerate
 * rule. Validates the inputs are integers in `[0, 100]`; throws on
 * out-of-range so tests catch programmer error rather than silently
 * clamping.
 */
export function formStateToRule(adminPct: number, everyonePct: number): FeatureGateRuleNode | null {
  assertValidPercent(adminPct, 'adminPct');
  assertValidPercent(everyonePct, 'everyonePct');

  if (adminPct === 0 && everyonePct === 0) {
    return null;
  }
  if (everyonePct === 0) {
    return { type: FEATURE_GATE_RULE_TYPE.IS_ADMIN, rolloutPercent: adminPct };
  }
  if (adminPct === 0) {
    return { type: FEATURE_GATE_RULE_TYPE.ALL_USERS, rolloutPercent: everyonePct };
  }
  return {
    type: FEATURE_GATE_RULE_TYPE.OR,
    children: [
      { type: FEATURE_GATE_RULE_TYPE.IS_ADMIN, rolloutPercent: adminPct },
      { type: FEATURE_GATE_RULE_TYPE.ALL_USERS, rolloutPercent: everyonePct },
    ],
  };
}

/**
 * Inverse of `formStateToRule`. Detects the three canonical shapes and
 * extracts the slider values; returns `isCanonical: false` with both
 * sliders at 0 for any other shape so the form can render its
 * non-canonical banner. Order-tolerant on the OR composite — `[is_admin,
 * all_users]` and `[all_users, is_admin]` both round-trip to the same
 * form state.
 */
export function ruleToFormState(rule: FeatureGateRuleNode): IRuleParseResult {
  if (rule.type === FEATURE_GATE_RULE_TYPE.IS_ADMIN) {
    return { adminPct: rule.rolloutPercent, everyonePct: 0, isCanonical: true };
  }
  if (rule.type === FEATURE_GATE_RULE_TYPE.ALL_USERS) {
    return { adminPct: 0, everyonePct: rule.rolloutPercent, isCanonical: true };
  }
  if (rule.type === FEATURE_GATE_RULE_TYPE.OR && rule.children.length === 2) {
    const admin = rule.children.find((c) => c.type === FEATURE_GATE_RULE_TYPE.IS_ADMIN);
    const everyone = rule.children.find((c) => c.type === FEATURE_GATE_RULE_TYPE.ALL_USERS);
    if (admin && everyone) {
      return { adminPct: admin.rolloutPercent, everyonePct: everyone.rolloutPercent, isCanonical: true };
    }
  }
  return { ...ZERO_FORM_STATE, isCanonical: false };
}

/**
 * Renders a one-line human-readable summary for a canonical rule tree.
 * Used by the list view to show e.g. `Admins 100% · Everyone 10%` next
 * to the gate name. Returns `Custom rule` for non-canonical trees so the
 * list never crashes when the v2 builder hasn't shipped yet.
 */
export function summarizeRule(rule: FeatureGateRuleNode): string {
  const parsed = ruleToFormState(rule);
  if (!parsed.isCanonical) {
    return 'Custom rule';
  }
  if (parsed.adminPct > 0 && parsed.everyonePct > 0) {
    return `Admins ${parsed.adminPct}% · Everyone ${parsed.everyonePct}%`;
  }
  if (parsed.adminPct > 0) {
    return `Admins ${parsed.adminPct}%`;
  }
  return `Everyone ${parsed.everyonePct}%`;
}

function assertValidPercent(pct: number, name: string): void {
  if (!Number.isInteger(pct) || pct < 0 || pct > 100) {
    throw new Error(`${name} must be an integer in [0, 100]; got ${pct}`);
  }
}
